import { useState, useEffect } from 'react';
import api from '../../api/client';
import { exportWorkbook } from '../../utils/export-xlsx';

// Actividad en vivo de culturedj.com: quién navega, en qué sección, desde
// dónde y qué presionó. Diseñado para ir a media columna junto al panel de
// oyentes: todo apilado y con scroll interno.
//
// "En vivo" = visto hace menos de 5 minutos (ping de presencia cada 30s).

const flagOf = (cc) =>
  cc && /^[A-Za-z]{2}$/.test(cc)
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : '🌐';

const deviceIcon = (t) => {
  if (t === 'tv') return '📺';
  if (t === 'mobile') return '📱';
  if (t === 'tablet') return '📲';
  return '🖥️';
};

const fmtAgo = (ts) => {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(Number(ts) || ts).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtClock = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtFull = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDur = (s) => {
  if (s == null || Number.isNaN(s)) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const stamp = () => new Date().toISOString().slice(0, 10);

// Modal "Registro completo": movimientos históricos (pageviews + clicks) y
// sesiones de visitantes ("desde" completos), con 7 días de retención.
function SiteLogModal({ onClose }) {
  const [tab, setTab] = useState('movimientos');
  const [events, setEvents] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    setEvents(null);
    setVisitors(null);
    setError(null);
    Promise.all([
      api.get('/api/stats/site-events', { params: { hours: 168, limit: 400 } }),
      api.get('/api/stats/site-visitors', { params: { hours: 168, limit: 200 } }),
    ])
      .then(([e, v]) => {
        if (!alive) return;
        setEvents(e.data.events || []);
        setVisitors(v.data.visitors || []);
      })
      .catch((err) => { if (alive) setError(err.response?.data?.error || err.message); });
    return () => { alive = false; };
  }, [refresh]);

  // ── Descarga Excel (.xlsx) ───────────────────────────────────────────
  // Un solo archivo con las dos hojas: Movimientos + Visitantes.
  const downloadXlsx = () => {
    const sheets = [];
    if (events && events.length) {
      sheets.push({
        name: 'Movimientos',
        rows: events.map((e) => ({
          'Fecha y hora': fmtFull(e.ts),
          'Tipo': e.action === 'click' ? 'Click' : 'Página vista',
          'Ciudad': e.city || '',
          'País': e.country || '',
          'ISP': e.isp || '',
          'Página': e.page || '',
          'Texto presionado': e.target_text || '',
          'Link presionado': e.target_href || '',
          'Vino desde': e.referrer || '',
          'Dispositivo': [e.os, e.browser].filter(Boolean).join(' '),
          'IP': e.ip_masked || '',
        })),
        widths: { 'Fecha y hora': 17, 'Página': 28, 'Texto presionado': 22, 'Link presionado': 22, 'Vino desde': 22, 'Dispositivo': 20, 'ISP': 20 },
      });
    }
    if (visitors && visitors.length) {
      sheets.push({
        name: 'Visitantes',
        rows: visitors.map((v) => ({
          'Última vez': fmtFull(v.last_seen),
          'Primera vez': fmtFull(v.first_seen),
          'Duración': fmtDur(v.duration_s),
          'Ciudad': v.city || '',
          'País': v.country || '',
          'ISP': v.isp || '',
          'Dispositivo': [v.os, v.browser].filter(Boolean).join(' '),
          'Páginas vistas': v.pages_seen,
          'Clicks': v.clicks,
          'Movimientos': v.events,
          'IP': v.ip_masked || '',
          'Sesión': v.sid,
        })),
        widths: { 'Última vez': 17, 'Primera vez': 17, 'ISP': 20, 'Dispositivo': 20, 'Sesión': 26 },
      });
    }
    if (sheets.length) {
      exportWorkbook(`culturedj-registro-${stamp()}.xlsx`, sheets);
    }
  };

  const tabBtn = (k, label) => (
    <button
      key={k}
      onClick={() => setTab(k)}
      className={`rounded px-2 py-1 text-[11px] font-medium ${
        tab === k
          ? 'border border-primary-700/50 bg-primary-900/30 text-primary-300'
          : 'border border-dark-700 bg-dark-800/60 text-dark-300 hover:bg-dark-800'
      }`}
    >
      {label}
    </button>
  );

  const th = 'px-2 py-1.5';
  const tdc = 'px-2 py-1 text-[11px]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="flex h-[82vh] w-[min(1100px,96vw)] flex-col overflow-hidden rounded-xl border border-dark-700 bg-dark-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dark-700 bg-dark-850 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-dark-100">📋 Registro completo · culturedj.com</h3>
          <div className="flex items-center gap-1.5">
            {tabBtn('movimientos', '🗂 Movimientos')}
            {tabBtn('sesiones', '👤 Visitantes')}
            <button
              onClick={downloadXlsx}
              title="Descargar registro completo en Excel (.xlsx): hojas Movimientos + Visitantes"
              className="rounded border border-success-700/50 bg-success-900/20 px-2 py-1 text-[11px] text-success-300 hover:bg-success-900/40"
            >
              ⬇ Excel
            </button>
            <button
              onClick={() => setRefresh(refresh + 1)}
              title="Actualizar"
              className="rounded border border-dark-700 bg-dark-800/60 px-2 py-1 text-[11px] text-dark-300 hover:bg-dark-800"
            >
              ↻
            </button>
            <button
              onClick={onClose}
              title="Cerrar"
              className="rounded border border-dark-700 bg-dark-800/60 px-2 py-1 text-[11px] text-dark-300 hover:bg-dark-800"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {error ? (
            <p className="p-4 text-sm text-error-400">Error: {error}</p>
          ) : events === null ? (
            <p className="p-4 text-sm text-dark-400">Cargando registro…</p>
          ) : tab === 'movimientos' ? (
            events.length === 0 ? (
              <p className="p-4 text-sm text-dark-400">Sin movimientos registrados todavía.</p>
            ) : (
              <table className="w-full min-w-[720px] text-left">
                <thead className="sticky top-0 bg-dark-800/95 text-[10px] uppercase text-dark-400">
                  <tr>
                    <th className={th}>Cuándo</th>
                    <th className={th}>Desde</th>
                    <th className={th}>Movimiento</th>
                    <th className={th}>Dispositivo</th>
                    <th className={th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <tr key={i} className="border-b border-dark-800/60 hover:bg-dark-800/40">
                      <td className={`${tdc} whitespace-nowrap text-dark-500`}>{fmtFull(e.ts)}</td>
                      <td className={`${tdc} whitespace-nowrap text-dark-200`}>
                        {flagOf(e.country_code)} {[e.city, e.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className={`${tdc} text-dark-200`}>
                        {e.action === 'click' ? (
                          <>clickeó <b className="text-primary-300">{e.target_text || '—'}</b> · {pageLink(e.page)}</>
                        ) : (
                          <>vió {pageLink(e.page)}</>
                        )}
                      </td>
                      <td className={`${tdc} whitespace-nowrap text-dark-400`}>
                        {[e.os, e.browser].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className={`${tdc} font-mono text-dark-500`}>{e.ip_masked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            visitors === null ? (
              <p className="p-4 text-sm text-dark-400">Cargando sesiones…</p>
            ) : visitors.length === 0 ? (
              <p className="p-4 text-sm text-dark-400">Sin sesiones registradas todavía.</p>
            ) : (
              <table className="w-full min-w-[820px] text-left">
                <thead className="sticky top-0 bg-dark-800/95 text-[10px] uppercase text-dark-400">
                  <tr>
                    <th className={th}>Última vez</th>
                    <th className={th}>Desde</th>
                    <th className={th}>Dispositivo</th>
                    <th className={th}>Páginas</th>
                    <th className={th}>Clicks</th>
                    <th className={th}>Duración</th>
                    <th className={th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v) => (
                    <tr key={v.sid} className="border-b border-dark-800/60 hover:bg-dark-800/40">
                      <td className={`${tdc} whitespace-nowrap text-dark-500`}>{fmtFull(v.last_seen)}</td>
                      <td className={`${tdc} whitespace-nowrap text-dark-200`} title={v.isp || undefined}>
                        {flagOf(v.country_code)} {[v.city, v.country].filter(Boolean).join(', ') || '—'}
                        {v.isp && <span className="text-dark-600"> · {v.isp.split(' ')[0]}</span>}
                      </td>
                      <td className={`${tdc} whitespace-nowrap text-dark-400`}>
                        {[v.os, v.browser].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className={`${tdc} tabular-nums text-dark-200`}>{v.pages_seen}</td>
                      <td className={`${tdc} tabular-nums text-dark-200`}>{v.clicks}</td>
                      <td className={`${tdc} tabular-nums text-dark-300`}>{fmtDur(v.duration_s)}</td>
                      <td className={`${tdc} font-mono text-dark-500`}>{v.ip_masked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        <div className="border-t border-dark-700 bg-dark-850 px-4 py-1.5 text-[10px] text-dark-500">
          Historial de 7 días ·{' '}
          {tab === 'movimientos'
            ? `${events?.length ?? 0} movimientos`
            : `${visitors?.length ?? 0} sesiones`}{' '}
          · actualizá con ↻
        </div>
      </div>
    </div>
  );
}

const shortPage = (p) => {
  if (!p) return '—';
  let s = String(p).split('?')[0];
  if (s.length > 22) s = s.slice(0, 20) + '…';
  return s || '/';
};

// Link violeta a la página del sitio donde entró/miró el visitante
const pageLink = (page) => (
  <a
    href={'https://culturedj.com' + String(page || '/')}
    target="_blank"
    rel="noreferrer"
    title={page}
    onClick={(e) => e.stopPropagation()}
    className="text-primary-300 underline decoration-primary-500/60 hover:text-primary-200"
  >
    {shortPage(page)}
  </a>
);

function D({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <span className="text-dark-500">{label}: </span>
      <span className={`break-all text-dark-200 ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function VisitorRow({ v }) {
  const [open, setOpen] = useState(false);
  const place = v.city || v.country
    ? `${flagOf(v.country_code)} ${v.city ? v.city + (v.region ? ', ' + v.region : '') : v.country}`
    : 'Sin ubicación';
  const action = v.last_action
    ? `presionó "${(v.last_action.text || '').slice(0, 30)}"`
    : null;
  return (
    <>
      <tr className="cursor-pointer border-b border-dark-800/60 hover:bg-dark-800/40" onClick={() => setOpen(!open)}>
        <td className="px-2 py-1 text-[11px] text-dark-200">{place}</td>
        <td className="px-2 py-1">
          <a
            href={'https://culturedj.com' + String(v.page || '/')}
            target="_blank"
            rel="noreferrer"
            title={v.page}
            onClick={(e) => e.stopPropagation()}
            className="inline-block max-w-[110px] truncate rounded bg-dark-800 px-1.5 py-0.5 text-[9px] text-primary-300 hover:text-primary-200 hover:underline"
          >
            {shortPage(v.page)}
          </a>
        </td>
        <td className="max-w-[130px] truncate px-2 py-1 text-[10px] text-dark-400" title={action || 'navegando…'}>
          {action || <span className="text-dark-600">navegando…</span>}
        </td>
        <td className="px-2 py-1 text-[10px] text-dark-500">{fmtAgo(v.last_seen)}</td>
      </tr>
      {open && (
        <tr className="border-b border-dark-800/60 bg-dark-900/60">
          <td colSpan={4} className="px-3 py-2">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[10px] md:grid-cols-3">
              <D label="Dispositivo" value={`${deviceIcon(v.device_type)} ${[v.os, v.browser].filter(Boolean).join(' ')}`} />
              <D label="Modelo" value={v.device_model} />
              <D label="IP" value={v.ip_masked} mono />
              <D label="ISP" value={v.isp} />
              <D label="En el sitio" value={fmtAgo(v.first_seen)} />
              <D label="Secciones vistas" value={v.pages} />
              <D label="Entró por" value={shortPage(v.entry_page)} mono />
              <D label="Vino desde" value={v.referrer ? shortPage(v.referrer) : 'directo'} mono />
              <D label="ID" value={v.sid} mono />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SiteActivityPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/api/stats/site-now');
        if (alive) { setData(res.data); setError(null); }
      } catch (e) {
        if (alive) setError(e.response?.data?.error || e.message);
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const visitors = data?.visitors || [];
  const events = data?.events || [];
  const pages = data?.pages_now || [];
  const cityTiles = (() => {
    const m = new Map();
    for (const v of visitors) {
      const label = v.city || v.country || null;
      const key = label ? `${label}|${v.country_code || ''}` : 'unknown';
      const e = m.get(key) || { key, label: label || 'Sin ubicación', cc: v.country_code, count: 0, page: null };
      e.count += 1;
      if (!e.page && v.page) e.page = v.page;
      m.set(key, e);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  })();

  return (
    <div className="card flex h-[450px] flex-col overflow-hidden">
      <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-dark-100">
          Actividad en culturedj.com
          <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-success-700/50 bg-success-900/20 px-1.5 py-0.5 text-[10px] font-normal text-success-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success-400"></span>
            {data?.online ?? 0} usuarios en culturedj
          </span>
        </h2>
        <span className="flex items-center gap-2">
          <button
            onClick={() => setLogOpen(true)}
            title="Registro completo: movimientos históricos y sesiones de visitantes (7 días)"
            className="rounded border border-primary-700/50 bg-primary-900/20 px-1.5 py-0.5 text-[10px] font-medium text-primary-300 hover:bg-primary-900/40"
          >
            📋 Registro completo
          </button>
          <span className="text-[10px] text-dark-500" title="Presencia por ping cada 30s; 'en vivo' = visto hace menos de 5 minutos">
            actualizado {data ? fmtClock(data.updated_at) : '—'}
          </span>
        </span>
      </div>

      {error && <p className="mb-1 shrink-0 text-[11px] text-error-400">Error: {error}</p>}

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">

      {/* Bloques grandes tipo cartel (mismo estilo que OYENTES en vivo) */}
      <div className="mb-2 grid grid-cols-2 gap-1.5">
        <div
          className="flex h-11 items-center justify-between gap-2 rounded-md border border-success-500 bg-success-600 px-3 shadow-[0_0_10px_rgba(22,163,74,.5)]"
          title="Visitantes navegando culturedj.com ahora (visto hace menos de 5 minutos)"
        >
          <span className="flex flex-col leading-tight">
            <span className="text-[9px] font-bold uppercase tracking-wider text-white">👥 En el sitio</span>
            <span className="text-[8px] text-white/80">culturedj.com</span>
          </span>
          <b className="text-2xl font-extrabold leading-none tabular-nums text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,.35)' }}>
            {data?.online ?? 0}
          </b>
        </div>
        <div
          className="flex h-11 items-center justify-between gap-2 rounded-md border border-blue-500 bg-blue-600 px-3 shadow-[0_0_10px_rgba(59,130,246,.4)]"
          title="Secciones distintas del sitio con gente ahora mismo"
        >
          <span className="flex flex-col leading-tight">
            <span className="text-[9px] font-bold uppercase tracking-wider text-white">🧭 Secciones</span>
            <span className="text-[8px] text-white/80">activas ahora</span>
          </span>
          <b className="text-2xl font-extrabold leading-none tabular-nums text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,.35)' }}>
            {pages.length}
          </b>
        </div>
      </div>

      {/* EN QUÉ PARTE ESTÁN: un bloque por sección con su gente */}
      <div className="mb-2">
        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-dark-300">📍 En qué parte están</span>
        <div className="flex flex-wrap gap-1.5">
          {pages.length === 0 ? (
            <span className="text-[11px] text-dark-400">Nadie navegando el sitio ahora.</span>
          ) : (
            pages.map((p) => (
              <a
                key={p.page}
                href={'https://culturedj.com' + String(p.page || '/')}
                target="_blank"
                rel="noreferrer"
                title={p.page}
                className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-dark-600 bg-black px-2.5 shadow-[0_0_8px_rgba(0,0,0,.5)] hover:border-primary-700"
              >
                <span className="max-w-[120px] truncate text-[10px] font-bold uppercase tracking-wide text-dark-200">
                  {shortPage(p.page)}
                </span>
                <b className="font-mono text-xl font-extrabold leading-none tabular-nums text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,.35)' }}>
                  {p.count}
                </b>
              </a>
            ))
          )}
        </div>
      </div>

      {/* DESDE DÓNDE ENTRAN: un bloque por ciudad de origen */}
      <div className="mb-2">
        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-dark-300">🌍 Desde dónde entran</span>
        <div className="flex flex-wrap gap-1.5">
          {cityTiles.length === 0 ? (
            <span className="text-[11px] text-dark-400">—</span>
          ) : (
            cityTiles.map((t) => (
              <span
                key={t.key}
                title={`${t.label} — ${t.count} visitante(s)${t.page ? ` · mirando ${t.page}` : ''}`}
                className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-dark-600 bg-black px-2.5 shadow-[0_0_8px_rgba(0,0,0,.5)]"
              >
                <span className="text-base leading-none">{t.key === 'unknown' ? '❔' : flagOf(t.cc)}</span>
                <span className="max-w-[110px] truncate text-[10px] font-bold uppercase tracking-wide text-dark-200">{t.label}</span>
                <b className="font-mono text-xl font-extrabold leading-none tabular-nums text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,.35)' }}>
                  {t.count}
                </b>
              </span>
            ))
          )}
        </div>
      </div>

      {/* Visitantes en vivo: desde dónde, qué miran y último movimiento */}
      <div className="overflow-hidden rounded-lg border border-dark-700">
        {visitors.length === 0 ? (
          <p className="p-2 text-[11px] text-dark-400">Nadie navegando el sitio ahora.</p>
        ) : (
          <div>
            <table className="w-full min-w-[420px] text-left">
              <thead className="sticky top-0 bg-dark-800/90 text-[9px] uppercase text-dark-400">
                <tr>
                  <th className="px-2 py-1">Desde</th>
                  <th className="px-2 py-1">Mirando</th>
                  <th className="px-2 py-1">Movimiento</th>
                  <th className="px-2 py-1">Visto</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => <VisitorRow key={v.sid} v={v} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feed de movimientos en vivo */}
      <div className="mt-1.5 overflow-hidden rounded-lg border border-dark-700">
        <div className="border-b border-dark-700 bg-dark-800/60 px-2 py-0.5 text-[9px] uppercase text-dark-400">
          Últimos movimientos
        </div>
        <div>
          {events.length === 0 ? (
            <p className="p-2 text-[11px] text-dark-400">Sin eventos todavía.</p>
          ) : (
            <ul className="divide-y divide-dark-800/60">
              {events.slice(0, 20).map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-2 px-2 py-1 text-[10px]">
                  <span className="min-w-0 truncate text-dark-200">
                    {flagOf(e.country_code)}{' '}
                    {e.action === 'click' ? (
                      <>clickeó <b className="text-primary-300">{(e.target_text || '—').slice(0, 24)}</b> · {pageLink(e.page)}</>
                    ) : (
                      <>vió {pageLink(e.page)}</>
                    )}
                  </span>
                  <span className="shrink-0 text-dark-500">{fmtClock(e.ts)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      </div>

      {logOpen && <SiteLogModal onClose={() => setLogOpen(false)} />}
    </div>
  );
}
